import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from "rxjs/operators";
import { ApiService } from './api.service';

import { Issue, IssueType, IssuesStatus, UpdateIssueRequest, IssueEscalationDecision, ProgramAreaGroupMap, IssueReassignment } from '../models/issue.model';
import { IUploadFile } from "./document-management.service";
import { ComplianceReportingType, ParseProductionDataResponse, ComplianceReport, ComplianceReportSearchSpec, ReportNote } from "../../issues/models/compliance.model";
import { ParserError, ParseProductionDataRequest } from "../../builder/components/state/controls/config/data/file-parser-config/parser.model";


export interface SearchSpecPaging {
    startingIndex: number;
    numberOfItemsToReturn: number;
}

export interface OrderParameters {
    field: string;
    ascending: boolean;
}

export interface IssueSearchSpec {
    ordering: OrderParameters;
    pagingCriteria?: SearchSpecPaging;
    ids?: string[];
    chiefsOrderIds?: string[];
    holdIds?: string[];
    issueIsResolved?: boolean;
    loadChildren: boolean;
    legacyIds?: string[];
    organizationIds: string[];
    wellIds?: string[];
    typeCodes?: string[];
    filters?: any;
    statuses: IssuesStatus[];
    dueDateFrom?: Date;
    dueDateTo?: Date;
    resolvedDateFrom?: Date;
    resolvedDateTo?: Date;
    createdDateFrom?: Date;
    createdDateTo?: Date;
    issueOwner?: string;
    loadOrganizationDetails?: boolean
    loadContactDetails?: boolean;
    loadWellDetails?: boolean;
    yearFrom?: number;
    yearTo?: number;
    quarters?: string[];
    includeIssuesByWellOwnership?: boolean;
}

export interface IssueSearchResult {

    apiNumber: string;
    contested: boolean;
    dateCreated: Date;
    dateDue: Date;
    dateResolved: Date;
    extensionGranted: boolean;
    issueCode: string;
    issueDescription: string;
    legacyOwnerNumber: string;
    nextActionDate: Date;
    organizationAddress: string;
    organizationCity: string;
    organizationName: string;
    organizationPOBox: string;
    organizationState: string;
    organizationSuite: string;
    organizationZipCode: string;
    ownerNumber: string;
    primaryContactEmail: string;
    primaryContactName: string;
    programArea: string;
    wellName: string;
    wellNumber: string;
    identifier: string;
}

@Injectable()
export class IssuesService {
    public BaseUrl = '/api/issues';

    constructor(
        public http: HttpClient,
        public apiService: ApiService
    ) { }

    saveIssue(issue: Issue): Observable<Issue> {
        const url = `${this.BaseUrl}/save`;
        let issueResponse = this.http.post<Issue>(url, issue);
        return issueResponse;
    }

    getIssueById(issueId: string): Observable<Issue> {
        const url = `${this.BaseUrl}/get-by-id/${issueId}`;
        return this.http.get<Issue>(url);
    }

    getIssues(searchSpec: IssueSearchSpec): Observable<Array<Issue>> {
        const url = `${this.BaseUrl}/search`;
        let issues = this.http.post<Array<Issue>>(url, searchSpec);
        return issues;
    }

    searchIssues(searchSpec: IssueSearchSpec): Observable<IssueSearchResult[]> {
        const url = `${this.BaseUrl}/detailed-search`
        return this.http.post<IssueSearchResult[]>(url, searchSpec);
    }

    getAllIssueTypes(): Observable<Array<IssueType>> {
        const url = `${this.BaseUrl}/getAllIssueTypes`;
        return this.http.get<IssueType[]>(url);
    }

    getComplianceReport(id: string): Observable<ComplianceReport> {
        const spec = new ComplianceReportSearchSpec(true);
        spec.ids = [id];
        return this.findComplianceReports(spec).pipe(map((report: ComplianceReport[]) => {
            if (report == null || report.length === 0) return null;
            return report[0];
        }));
    }

    findComplianceReports(spec: ComplianceReportSearchSpec): Observable<ComplianceReport[]> {
        const url = `${this.BaseUrl}/findComplianceReports`;        
        return this.http.post<ComplianceReport[]>(url, spec);
    }

    findRelatedIssues(reportId: string): Observable<Issue[]> {
        const url = `${this.BaseUrl}/find-related-issues/${reportId}`;
        return this.http.get<Issue[]>(url);
    }

    getComplianceReportingTypes(enabledOnly: boolean = true): Observable<ComplianceReportingType[]> {
        const url = `${this.BaseUrl}/getComplianceReportingTypes/${enabledOnly}`;
        return this.http.get<ComplianceReportingType[]>(url);
    }

    parseProductionFile(organizationId: string, file: IUploadFile): Observable<ParseProductionDataResponse> {
        const url = `${this.BaseUrl}/parseProductionReports`;
        const request = { file: file, organizationId: organizationId } as ParseProductionDataRequest;
        return this.http.post<ParseProductionDataResponse>(url, request);
    }

    saveComplianceReport(report: ComplianceReport): Observable<string> {
        const url = `${this.BaseUrl}/saveComplianceReport`;
        return this.http.post<string>(url, report);
    }

    saveReportNote(note: ReportNote): Observable<ReportNote> {
        const url = `${this.BaseUrl}/saveNote`;
        return this.http.post<ReportNote>(url, note);
    }

    updateIssue(request: UpdateIssueRequest): Observable<Issue> {
        const url = `${this.BaseUrl}/update-issue`;
        return this.http.post<Issue>(url, request);
    }

    createIssue(issue: Issue): Observable<Issue> {
        const url = `${this.BaseUrl}/create`;
        return this.http.post<Issue>(url, issue);
    }

    decisionIssueEscalation(decision: IssueEscalationDecision): Observable<Issue> {
        const url = `${this.BaseUrl}/decide-escalation`
        return this.http.post<Issue>(url, decision);
    }

    getGroupMaps(): Observable<ProgramAreaGroupMap[]> {
        const url = `${this.BaseUrl}/get-group-maps`;
        return this.http.get <ProgramAreaGroupMap[]>(url);
    }

    validateProductionData(data: ParseProductionDataResponse): Observable<ParserError[]> {
        const url = `${this.BaseUrl}/validateProductionData`;
        return this.http.post<ParserError[]>(url, data);
    }

    assignIssue(issueId: string, assignee: IssueReassignment): Observable<Issue> {
        const url = `${this.BaseUrl}/assign-issue`;
        return this.http.post<Issue>(url, { issue: issueId, assignment: assignee });
    }
}
